/**
 * Drift score history.
 *
 * Each `cai check` run appends one entry to `.cai/.cache/drift-history.jsonl`.
 * `cai check --history` reads it back, shows trend over time, and answers the
 * question "is my scaffold getting better or worse?"
 *
 * Same opt-out as the rest of the telemetry: CAI_NO_TELEMETRY=1 disables it.
 * Same append-only design — never blocks the check itself, never throws.
 */
import { appendFileSync, existsSync, mkdirSync, readFileSync, statSync, renameSync } from "node:fs";
import { dirname, join } from "node:path";

export interface DriftHistoryEntry {
  ts: number;
  score: number;
  weightedScore?: number;
  errors: number;
  warnings: number;
  filesChecked: number;
}

const CACHE_DIR = join(".cai", ".cache");
const HISTORY_FILE = "drift-history.jsonl";
const ROTATE_AT_BYTES = 256 * 1024; // ~2000 entries before rotation

function isDisabled(): boolean {
  return Boolean(process.env.CAI_NO_TELEMETRY);
}

function historyPath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, HISTORY_FILE);
}

/** Append a drift report snapshot. Never throws. */
export function appendHistory(projectRoot: string, entry: Omit<DriftHistoryEntry, "ts">): void {
  if (isDisabled()) return;
  try {
    const path = historyPath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });

    const line = JSON.stringify({ ts: Date.now(), ...entry }) + "\n";
    appendFileSync(path, line, "utf8");

    // Cheap rotation check — only stat occasionally to keep this hot path fast
    if (Math.random() < 0.02) {
      maybeRotate(path);
    }
  } catch {
    // never fail the check itself over history
  }
}

function maybeRotate(path: string): void {
  try {
    const size = statSync(path).size;
    if (size < ROTATE_AT_BYTES) return;
    renameSync(path, `${path}.old`);
  } catch {
    // ignore
  }
}

/** Read all history entries, oldest first. */
export function readHistory(projectRoot: string): DriftHistoryEntry[] {
  const path = historyPath(projectRoot);
  if (!existsSync(path)) return [];

  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    return [];
  }

  const entries: DriftHistoryEntry[] = [];
  for (const line of raw.split("\n")) {
    if (!line) continue;
    try {
      entries.push(JSON.parse(line) as DriftHistoryEntry);
    } catch {
      // skip malformed line
    }
  }
  return entries.sort((a, b) => a.ts - b.ts);
}

/**
 * Render a tiny ASCII sparkline from a series of scores.
 * Uses Unicode block characters for 8 levels.
 */
const SPARK_CHARS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

export function renderSparkline(values: number[]): string {
  if (values.length === 0) return "";
  if (values.length === 1) return SPARK_CHARS[7];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return values
    .map((v) => {
      const idx = Math.round(((v - min) / range) * (SPARK_CHARS.length - 1));
      return SPARK_CHARS[idx];
    })
    .join("");
}

/** Compute simple trend stats over the history. */
export interface TrendSummary {
  count: number;
  current: number;
  previous: number | null;
  delta: number | null;
  best: number;
  worst: number;
  average: number;
  sparkline: string;
  /** Score change per run over the last N runs (negative = decaying). null when <3 entries. */
  velocity: number | null;
  /** Number of runs used to compute velocity. */
  velocityWindow: number;
}

/** Compute linear regression slope over the last N scores (velocity = points/run). */
function computeVelocity(scores: number[], window = 5): number {
  const series = scores.slice(-window);
  const n = series.length;
  if (n < 2) return 0;

  // Simple linear regression: slope = (n·Σxy - Σx·Σy) / (n·Σx² - (Σx)²)
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += series[i];
    sumXY += i * series[i];
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return 0;
  return Math.round(((n * sumXY - sumX * sumY) / denom) * 10) / 10;
}

export function summarizeHistory(entries: DriftHistoryEntry[]): TrendSummary | null {
  if (entries.length === 0) return null;

  const scores = entries.map((e) => e.weightedScore ?? e.score);
  const current = scores[scores.length - 1];
  const previous = scores.length > 1 ? scores[scores.length - 2] : null;
  const delta = previous !== null ? current - previous : null;
  const sum = scores.reduce((a, b) => a + b, 0);

  // Show last 30 entries for the sparkline so it doesn't get too wide
  const sparkSeries = scores.slice(-30);

  const velocityWindow = Math.min(5, scores.length);
  const velocity = scores.length >= 3 ? computeVelocity(scores) : null;

  return {
    count: entries.length,
    current,
    previous,
    delta,
    best: Math.max(...scores),
    worst: Math.min(...scores),
    average: Math.round(sum / scores.length),
    sparkline: renderSparkline(sparkSeries),
    velocity,
    velocityWindow,
  };
}
